import { useCallback, useState, useMemo } from "react";
import { Spin } from "antd";
import TopMenu from "components/OperationsEndOfDay/TopMenu";
import AlertIcon from "components/icons/AlertIcon";
import CustomModalAlerts from "components/common/ModalAlerts";
import { utils, writeFile } from "xlsx";
import useZReports from "graphql/useZReports";
import moment from "moment";
import { usePaginatedData } from "helpers/hooks";
import { useTranslation } from "react-i18next";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "components/common/BottomPageSelectorWithPagination";
import { getDateHeader } from "helpers/dataHelper";
import { dbValueToFloat, printDocumentPDF } from "helpers/utils";
import CheckboxTable from "components/common/CheckboxTable";
import DocumentPDF from "./DocumentPDF";
import List from "../../components/common/ListWithMoreOptions";
import Bill from "./Bill";
import s from "../../styles/Operations.styles";
import useXReports from "../../graphql/useXReports";
import { useBusinessContext } from "../../BusinessApp";

const SCAPE_VALUE = "\\";

function fixValueForQuery(value) {
  return value
    .replace(`${SCAPE_VALUE}`, `${SCAPE_VALUE}${SCAPE_VALUE}`)
    .replace("*", `${SCAPE_VALUE}*`);
}

function OperationsEndOfDay() {
  const { t } = useTranslation();
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [billToView, setBillToView] = useState(null);
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(false);
  const [billType, setBillType] = useState("all");
  const [terminal, setTerminal] = useState("all");
  const [users, setUsers] = useState("all");

  const [date, setDate] = useState(() => ({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  }));

  const [selected, setSelected] = useState([]);

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination("name");

  const { storeId, storeInfo, currencyFormat } = useBusinessContext();

  const { zReports, loading: loading1 } = useZReports(storeId, date?.range);
  const { xReports, loading: loading2 } = useXReports(storeId, date?.range);

  const onClose = () => {
    setVisible(false);
  };

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const data = useMemo(() => {
    const filterReports = (reports) =>
      reports.filter(
        (i) =>
          (billType === "all" || billType === i.type) &&
          (terminal === "all" || terminal === i.terminalSerial) &&
          (users === "all" || users === i.closeUserName)
      );

    const cashManagementsListFilteredProfile = filterReports(zReports);
    const cashManagementsListFilteredProfilex = filterReports(xReports);

    const filteredZReports = cashManagementsListFilteredProfile.filter((i) => {
      const regex = new RegExp(fixValueForQuery(search), "i");
      return (
        (i.reportNumber && i.reportNumber.match(regex)) ||
        (i.terminalSerial && i.terminalSerial.match(regex))
      );
    });

    const filteredXReports = cashManagementsListFilteredProfilex.filter((i) => {
      const regex = new RegExp(fixValueForQuery(search), "i");
      return (
        (i.reportNumber && i.reportNumber.match(regex)) ||
        (i.terminalSerial && i.terminalSerial.match(regex))
      );
    });

    return [...filteredZReports, ...filteredXReports];
  }, [zReports, xReports, billType, terminal, users, search]);

  const dataToShow = usePaginatedData(data, pagination, sorting, (item) => [
    <CheckboxTable
      key={item._id}
      name={moment(item.creationDatetime).format("llll")}
      checked={selected.find((curr) => curr === item._id)}
      setChecked={(value) => {
        if (value) setSelected([...selected, item._id]);
        else {
          const newSelected = [...selected];
          const idx = selected.findIndex((curr) => curr === item._id);
          if (idx >= 0) {
            newSelected.splice(idx, 1);
          }
          setSelected(newSelected);
        }
      }}
    />,
    item.closeDatetime
      ? moment(item.closeDatetime).format("llll")
      : moment(item.updateDatetime).format("llll"),
    t(`containers.Operations.${item.type}Report`),
    item.reportNumber,
    item.terminalSerial,
    `${currencyFormat(parseFloat(item.facTotal) - parseFloat(item.creTotal))}`,
  ]);



  const selectedReports = useMemo(() => {
    if (selected.length === 0) {
      return data;
    }
    return data.filter((element) => {
      if (selected.includes(element._id)) {
        return element;
      }
    });
  }, [selected, data]);

  const dataToPrint = useMemo(
    () =>
      selectedReports.map((item) => [
        `${item.type}-${item.terminalSerial}-${item.reportNumber}`,
        moment(item.closeDatetime).format("MM/DD/YYYY"),
        t(`containers.Operations.${item.type}Report`),
        dbValueToFloat(item.cardAmount),
        dbValueToFloat(item.cardQuantity),
        dbValueToFloat(item.cashAmount),
        dbValueToFloat(item.discountQuantity),
        dbValueToFloat(item.cashStartAmount),
        dbValueToFloat(item.cashEndAmount),
        item.facStartNumber,
        item.facEndNumber,
        dbValueToFloat(item.facQuantity),
        dbValueToFloat(item.facBase),
        dbValueToFloat(item.facTotal),
        dbValueToFloat(item.creStartNumber),
        dbValueToFloat(item.creEndNumber),
        dbValueToFloat(item.discountRefundQuantity),
        dbValueToFloat(item.creBase),
        dbValueToFloat(item.creTotal),
        item.openUserName,
        item.closeUserName,
        dbValueToFloat(item.discountAmount),
        dbValueToFloat(item.discountQuantity),
        dbValueToFloat(item.discountRefundAmount),
        dbValueToFloat(item.discountRefundQuantity),
        dbValueToFloat(item.itemQuantity),
        dbValueToFloat(item.itemRefundQuantity),
        dbValueToFloat(item.otherAmount),
        dbValueToFloat(item.otherQuantity),
        dbValueToFloat(item.otherStartAmount),
        dbValueToFloat(item.otherEndAmount),
      ]),
    [selectedReports]
  );

  const dataToPrintPdf = useMemo(
    () =>
      selectedReports.map((item) => [
        moment(item.creationDatetime).format("llll"),
        item.closeDatetime
          ? moment(item.closeDatetime).format("llll")
          : moment(item.updateDatetime).format("llll"),
        t(`containers.Operations.${item.type}Report`),
        item.reportNumber,
        item.terminalSerial,
        dbValueToFloat(item.facTotal - item.creTotal),
      ]),
    [selectedReports]
  );

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t("containers.Operations.Bill.closeOf")}  ${getDateHeader(
          date.range.start,
          date.range.end
        )}`,
      ],
      [
        t("containers.Operations.Bill.number"),
        t("containers.Operations.Bill.close"),
        t("containers.Operations.Bill.type"),
        t("containers.Operations.Bill.card_total"),
        t("containers.Operations.Bill.card_operations"),
        t("containers.Operations.Bill.cash_total"),
        t("containers.Operations.Bill.cash_operations"),
        t("containers.Operations.Bill.cash_start"),
        t("containers.Operations.Bill.cash_end"),
        t("containers.Operations.Bill.invoice_start"),
        t("containers.Operations.Bill.invoice_end"),
        t("containers.Operations.Bill.sales_operations"),
        t("containers.Operations.Bill.gross_invoice_total"),
        t("containers.Operations.Bill.net_invoice_total"),
        t("containers.Operations.Bill.return_start"),
        t("containers.Operations.Bill.return_end"),
        t("containers.Operations.Bill.return_operations"),
        t("containers.Operations.Bill.gross_return_total"),
        t("containers.Operations.Bill.net_return_total"),
        t("containers.Operations.Bill.opening_user"),
        t("containers.Operations.Bill.closing_user"),
        t("containers.Operations.Bill.discounts_total"),
        t("containers.Operations.Bill.discounts_count"),
        t("containers.Operations.Bill.returned_discounts_count"),
        t("containers.Operations.Bill.returned_discounts_total"),
        t("containers.Operations.Bill.item_total"),
        t("containers.Operations.Bill.returned_item_total"),
        t("containers.Operations.Bill.other_operations"),
        t("containers.Operations.Bill.other_total"),
        t("containers.Operations.Bill.other_start"),
        t("containers.Operations.Bill.other_end"),
      ],
      ...dataToPrint,
    ];

    const workbook = utils.book_new();
    const sheetName = t("containers.Operations.Bill.closes");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(workbook, `${t("containers.Operations.Bill.closes")}.xlsx`);
  }, [date.range.start, date.range.end, dataToPrint, t]);

  const getImageGraph = useCallback(async () => {
    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end),
          dataToPrintPdf,
          currencyFormat
        }}
      />,
      `${t("containers.Operations.Bill.closes")}.pdf`
    );
  }, [dataToPrintPdf, date.range.end, date.range.start]);

  const loading = loading1 || loading2;

  return (
    <s.PageContainer>
      <TopMenu
        date={date}
        setDate={setDate}
        selectedFilter={filter}
        data={zReports}
        setSearch={setSearch}
        changeSelectedFilter={(v) => setFilter(v)}
        changeSearchValue={(v) => changeSearch(v)}
        billType={billType}
        setBillType={setBillType}
        terminal={terminal}
        setTerminal={setTerminal}
        users={users}
        setUsers={setUsers}
        loading={loading}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={dataToPrint.length === 0}
      />
      <s.ListContainer>
        {/* show no content yet */}
        {!loading && zReports.length === 0 && (
          <s.Nocash_managementContainer>
            <s.Nocash_managementMessageCenter>
              <AlertIcon styles={{ display: "block", marginBottom: "7px" }} />
              {t("containers.Operations.notFound")}
            </s.Nocash_managementMessageCenter>
          </s.Nocash_managementContainer>
        )}

        <Bill
          visible={visible}
          onClose={onClose}
          data={billToView}
          storeInfo={storeInfo}
          storeId={storeId}
          setconfirmationModal={setConfirmationModal}
        />

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Spin
              style={{ color: "#000" }}
              size="large"
              tip={t("general.loading")}
            />
          </div>
        )}

        {zReports?.length > 0 && (
          <List
            width="1000px"
            handleShowDetails={(CashManagementList) => {
              setBillToView(CashManagementList.element);
              setVisible(true);
            }}
            showDetails
            data={dataToShow}
            checkboxOption
            selected={selected}
            setSelected={setSelected}
            columns={[
              {
                title: t("containers.Operations.opening"),
                sortable: true,
                columnId: "creationDatetime",
              },
              {
                title: t("containers.Operations.closing"),
                sortable: true,
                columnId: "closeDatetime",
              },
              {
                title: t("containers.Operations.typeClose"),
                sortable: true,
                columnId: "type",
              },
              {
                title: t("containers.Operations.number"),
                sortable: true,
                columnId: "reportNumber",
              },
              {
                title: t("containers.Operations.terminal"),
                sortable: true,
                columnId: "terminalSerial",
              },
              {
                title: t("containers.Operations.grossSales"),
                sortable: true,
                columnId: "facTotal",
              },
              {
                title: "",
              },
            ]}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{
              gridTemplateColumns: "10fr 10fr 7fr 6fr 6fr 6fr 3fr",
            }}
          />
        )}
      </s.ListContainer>
      {!loading && (
        <BottomPageSelectorWithPagination
          pagination={pagination}
          setPagination={setPagination}
          length={data.length}
        />
      )}

      <CustomModalAlerts
        visible={confirmationModal}
        width={428}
        height={234}
        centered
        titleAlert={[t("containers.Operations.fileExportedSuccessfully")]}
        info1={[t("containers.Operations.finddownload")]}
        button1={[t("containers.Operations.accept")]}
        onclick1={setConfirmationModal}
      />
    </s.PageContainer>
  );
}

export default OperationsEndOfDay;
